import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';

import { cn } from '@/utils';

interface CardProps {
  children: ReactNode;
  className?: string;
  hoverable?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  gradient?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export function Card({
  children,
  className = '',
  hoverable = false,
  clickable = false,
  onClick,
  gradient = false,
  padding = 'md',
}: CardProps) {
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'relative rounded-2xl overflow-hidden',
        'glass-card shadow-glass',
        paddingClasses[padding],
        hoverable && 'hover:shadow-lifted',
        clickable && 'cursor-pointer active:scale-[0.98]',
        gradient && 'bg-gradient-glass',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function StatsCard({
  icon: Icon,
  label,
  value,
  trend,
  trendDirection,
  color = 'violet',
}: {
  icon: LucideIcon;
  label: string;
  value: string | number;
  trend?: string;
  trendDirection?: 'up' | 'down' | 'neutral';
  color?: 'violet' | 'cyan' | 'emerald' | 'amber' | 'rose';
}) {
  const colorMap = {
    violet: {
      bg: 'bg-primary-500/10',
      icon: 'text-primary-400',
      value: 'text-primary-300',
    },
    cyan: {
      bg: 'bg-info-500/10',
      icon: 'text-info-400',
      value: 'text-info-300',
    },
    emerald: {
      bg: 'bg-success-500/10',
      icon: 'text-success-400',
      value: 'text-success-300',
    },
    amber: {
      bg: 'bg-warning-500/10',
      icon: 'text-warning-400',
      value: 'text-warning-300',
    },
    rose: {
      bg: 'bg-error-500/10',
      icon: 'text-error-400',
      value: 'text-error-300',
    },
  };

  const trendColors = {
    up: 'text-success-500',
    down: 'text-error-500',
    neutral: 'text-khiimori-text-muted',
  };

  const colors = colorMap[color];

  return (
    <div className="relative p-6 rounded-2xl overflow-hidden glass-card shadow-glass border border-khiimori-border">
      <div className="flex items-start justify-between mb-4">
        <div className="p-3 rounded-xl bg-khiimori-bg-tertiary/50">
          <Icon className={`w-5 h-5 ${colors.icon}`} />
        </div>

        {trend && trendDirection && (
          <div className={`text-sm font-medium ${trendColors[trendDirection]}`}>
            {trendDirection === 'up' && '↑ '}
            {trendDirection === 'down' && '↓ '}
            {trend}
          </div>
        )}
      </div>

      <div className={`text-3xl font-bold mb-1 ${colors.value}`}>{value}</div>
      <div className="text-khiimori-text-muted text-sm">{label}</div>

      <div className={`absolute -bottom-1 -right-1 w-24 h-24 rounded-full opacity-30 blur-2xl ${colors.bg}`} />
    </div>
  );
}

export function FeatureCard({
  icon: Icon,
  title,
  description,
  available = true,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  available?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'relative p-6 rounded-2xl overflow-hidden',
        'bg-khiimori-bg-elevated shadow-glass border border-khiimori-border/10',
        onClick && 'cursor-pointer hover:shadow-lifted hover:border-khiimori-border/20',
        !available && 'opacity-60',
      )}
    >
      <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center bg-primary-500/20">
        <Icon className="w-6 h-6 text-primary-400" />
      </div>

      <h3 className="text-lg font-semibold text-khiimori-text-primary mb-2 flex items-center gap-2">
        {title}
        {!available && (
          <span className="px-2 py-0.5 text-xs rounded-full bg-warning-500/20 text-warning-500 border border-warning-500/30">
            Coming Soon
          </span>
        )}
      </h3>

      <p className="text-khiimori-text-primary/50 text-sm leading-relaxed">{description}</p>
    </div>
  );
}
